import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { toast } from 'sonner'
import {
  ArrowUpRight,
  Phone,
  Power,
  PowerOff,
  Search,
  ShieldCheck,
  Store,
  Trash2,
  UserCog,
  UserPlus,
  UserRound,
  Users,
} from 'lucide-react'
import {
  activateAdministrador,
  createAdministrador,
  createMember,
  deactivateAdministrador,
  fetchAdministradores,
  fetchEmpresa,
  fetchOperadores,
  removeAdministrador,
  removeMember,
  transferEmpresaOwnership,
  updateAdministradorSociedades,
  type Administrador,
  type Operador,
} from '../../api'
import { useAuth } from '../../AuthContext'
import { PageHeader } from '../../components/PageHeader'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Badge } from '../../components/ui/badge'
import { Skeleton } from '../../components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { Card, CardContent } from '../../components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs'
import { Pagination } from '../../components/ui/pagination'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table'
import { UserAvatar } from '../../components/UserAvatar'
import { OperatorAssignmentsDialog } from '../../components/OperatorAssignmentsDialog'

const emptyForm = { nombre: '', email: '', password: '', sociedad_ids: [] as string[] }
const emptyOperForm = { nombre: '', email: '', password: '', sociedad_id: '' }
const PAGE_SIZE = 8

function matches(query: string, ...fields: string[]): boolean {
  if (!query.trim()) return true
  const q = query.trim().toLowerCase()
  return fields.some((f) => f.toLowerCase().includes(q))
}

const STAT_ACCENTS = {
  sociedades: 'from-sky-500/20 text-sky-600 ring-sky-500/30 dark:text-sky-300',
  administradores: 'from-violet-500/20 text-violet-600 ring-violet-500/30 dark:text-violet-300',
  operadores: 'from-emerald-500/20 text-emerald-600 ring-emerald-500/30 dark:text-emerald-300',
}

function StatCard({
  icon: Icon,
  accent,
  value,
  label,
}: {
  icon: typeof Store
  accent: string
  value: number | undefined
  label: string
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 py-5">
        <div
          className={`flex size-12 shrink-0 items-center justify-center rounded-xl bg-linear-to-br ring-1 ${accent}`}
        >
          <Icon className="size-5" strokeWidth={2} />
        </div>
        <div className="flex flex-col gap-0.5">
          <p className="text-2xl leading-none font-semibold tracking-tight">{value ?? '—'}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  )
}

export function AdministradoresPage() {
  const { user, activeSociedadId, refreshUser } = useAuth()
  const queryClient = useQueryClient()
  const empresaId = user?.sociedades.find((s) => s.id === activeSociedadId)?.empresa_id

  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [error, setError] = useState<string | null>(null)
  const [editTarget, setEditTarget] = useState<Administrador | null>(null)
  const [editSociedadIds, setEditSociedadIds] = useState<string[]>([])
  const [transferTarget, setTransferTarget] = useState<Administrador | null>(null)
  const [operOpen, setOperOpen] = useState(false)
  const [operForm, setOperForm] = useState(emptyOperForm)
  const [operError, setOperError] = useState<string | null>(null)
  const [assignTarget, setAssignTarget] = useState<Operador | null>(null)

  const [tab, setTab] = useState<'administradores' | 'operadores'>('administradores')
  const [search, setSearch] = useState('')
  const [adminPage, setAdminPage] = useState(1)
  const [operPage, setOperPage] = useState(1)

  const empresaQuery = useQuery({
    queryKey: ['empresa', empresaId],
    queryFn: () => fetchEmpresa(empresaId!),
    enabled: !!empresaId,
  })
  const administradoresQuery = useQuery({
    queryKey: ['administradores', empresaId],
    queryFn: () => fetchAdministradores(empresaId!),
    enabled: !!empresaId,
  })
  const operadoresQuery = useQuery({
    queryKey: ['operadores', empresaId],
    queryFn: () => fetchOperadores(empresaId!),
    enabled: !!empresaId,
  })

  useEffect(() => {
    if (editTarget) setEditSociedadIds(editTarget.sociedad_ids)
  }, [editTarget])

  useEffect(() => {
    setAdminPage(1)
    setOperPage(1)
  }, [search, tab])

  function invalidate(): void {
    queryClient.invalidateQueries({ queryKey: ['administradores', empresaId] })
  }

  function invalidateOperadores(): void {
    queryClient.invalidateQueries({ queryKey: ['operadores', empresaId] })
  }

  const createMutation = useMutation({
    mutationFn: () => createAdministrador(empresaId!, form),
    onSuccess: (admin) => {
      invalidate()
      setOpen(false)
      setForm(emptyForm)
      toast.success(`Se agregó a ${admin.nombre} como Administrador — pasale el email y la contraseña para que entre.`)
    },
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setError('Ya existe una cuenta con ese email.')
      } else {
        setError('No se pudo crear el Administrador. Revisá los datos e intentá de nuevo.')
      }
    },
  })

  const updateSociedadesMutation = useMutation({
    mutationFn: () => updateAdministradorSociedades(empresaId!, editTarget!.user_id, editSociedadIds),
    onSuccess: () => {
      invalidate()
      setEditTarget(null)
      toast.success('Actualizamos las Sociedades asignadas.')
    },
    onError: () => toast.error('No se pudieron actualizar las Sociedades asignadas.'),
  })

  const removeMutation = useMutation({
    mutationFn: (userId: string) => removeAdministrador(empresaId!, userId),
    onSuccess: invalidate,
    onError: () => toast.error('No se pudo quitar al Administrador.'),
  })

  const toggleActiveMutation = useMutation({
    mutationFn: (admin: Administrador) =>
      admin.is_active ? deactivateAdministrador(empresaId!, admin.user_id) : activateAdministrador(empresaId!, admin.user_id),
    onSuccess: (updated) => {
      invalidate()
      toast.success(updated.is_active ? `${updated.nombre} reactivado.` : `${updated.nombre} desactivado — ya no puede entrar a la plataforma.`)
    },
    onError: () => toast.error('No se pudo cambiar el estado del Administrador.'),
  })

  const transferMutation = useMutation({
    mutationFn: (userId: string) => transferEmpresaOwnership(empresaId!, userId),
    onSuccess: async () => {
      invalidate()
      setTransferTarget(null)
      await refreshUser()
      toast.success('Transferimos la titularidad de la Empresa.')
    },
    onError: () => {
      toast.error('No se pudo transferir la titularidad.')
      setTransferTarget(null)
    },
  })

  const createOperMutation = useMutation({
    mutationFn: () =>
      createMember(operForm.sociedad_id, {
        nombre: operForm.nombre,
        email: operForm.email,
        password: operForm.password,
        rank: 'operador',
      }),
    onSuccess: (op) => {
      invalidateOperadores()
      setOperOpen(false)
      setOperForm(emptyOperForm)
      toast.success(`Se agregó a ${op.nombre} como Operador — pasale el email y la contraseña para que entre.`)
    },
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setOperError('Ya existe una cuenta con ese email.')
      } else {
        setOperError('No se pudo agregar al Operador. Revisá los datos e intentá de nuevo.')
      }
    },
  })

  const removeOperMutation = useMutation({
    mutationFn: (op: Operador) => removeMember(op.sociedad_id, op.user_id),
    onSuccess: invalidateOperadores,
    onError: () => toast.error('No se pudo quitar al Operador.'),
  })

  function handleOperSubmit(e: FormEvent): void {
    e.preventDefault()
    setOperError(null)
    createOperMutation.mutate()
  }

  function handleSubmit(e: FormEvent): void {
    e.preventDefault()
    setError(null)
    createMutation.mutate()
  }

  function toggleSociedad(id: string): void {
    setForm((f) => ({
      ...f,
      sociedad_ids: f.sociedad_ids.includes(id) ? f.sociedad_ids.filter((x) => x !== id) : [...f.sociedad_ids, id],
    }))
  }

  function toggleEditSociedad(id: string): void {
    setEditSociedadIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]))
  }

  const sociedadNombrePorId = new Map((empresaQuery.data?.sociedades ?? []).map((s) => [s.id, s.nombre]))

  const filteredAdmins = useMemo(
    () => (administradoresQuery.data ?? []).filter((a) => matches(search, a.nombre, a.email)),
    [administradoresQuery.data, search],
  )
  const filteredOpers = useMemo(
    () => (operadoresQuery.data ?? []).filter((o) => matches(search, o.nombre, o.email, o.sociedad_nombre)),
    [operadoresQuery.data, search],
  )

  const adminPageCount = Math.max(1, Math.ceil(filteredAdmins.length / PAGE_SIZE))
  const operPageCount = Math.max(1, Math.ceil(filteredOpers.length / PAGE_SIZE))
  const pagedAdmins = filteredAdmins.slice((adminPage - 1) * PAGE_SIZE, adminPage * PAGE_SIZE)
  const pagedOpers = filteredOpers.slice((operPage - 1) * PAGE_SIZE, operPage * PAGE_SIZE)

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={ShieldCheck}
        title="Equipo"
        description="Administradores y Operadores de toda tu Empresa, en un solo lugar."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={Store} accent={STAT_ACCENTS.sociedades} value={empresaQuery.data?.sociedades.length} label="Sociedades" />
        <StatCard
          icon={Users}
          accent={STAT_ACCENTS.administradores}
          value={administradoresQuery.data?.length}
          label="Administradores"
        />
        <StatCard icon={UserRound} accent={STAT_ACCENTS.operadores} value={operadoresQuery.data?.length} label="Operadores" />
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'administradores' | 'operadores')}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="administradores">
              <ShieldCheck className="size-3.5" />
              Administradores
              {administradoresQuery.data && (
                <Badge variant="secondary" className="ml-1">
                  {administradoresQuery.data.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="operadores">
              <UserRound className="size-3.5" />
              Operadores
              {operadoresQuery.data && (
                <Badge variant="secondary" className="ml-1">
                  {operadoresQuery.data.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-56 pl-8"
              />
            </div>

            {tab === 'administradores' ? (
              <Dialog open={open} onOpenChange={(next) => { setOpen(next); if (!next) setForm(emptyForm) }}>
                <DialogTrigger asChild>
                  <Button disabled={!empresaQuery.data?.sociedades.length}>
                    <UserPlus className="size-4" />
                    Nuevo Administrador
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <DialogHeader>
                      <DialogTitle>Nuevo Administrador</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="admin-nombre">Nombre</Label>
                      <Input
                        id="admin-nombre"
                        required
                        value={form.nombre}
                        onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="admin-email">Email</Label>
                      <Input
                        id="admin-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="admin-password">Contraseña</Label>
                      <Input
                        id="admin-password"
                        type="text"
                        required
                        minLength={8}
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Mínimo 8 caracteres. Pasale este email y contraseña a la persona para que entre.
                      </p>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label className="mb-1">Sociedades a cargo</Label>
                      <div className="flex max-h-40 flex-col gap-1.5 overflow-y-auto rounded-md border p-2">
                        {empresaQuery.data?.sociedades.map((s) => (
                          <label key={s.id} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted">
                            <input
                              type="checkbox"
                              checked={form.sociedad_ids.includes(s.id)}
                              onChange={() => toggleSociedad(s.id)}
                            />
                            {s.nombre}
                          </label>
                        ))}
                      </div>
                    </div>

                    {error && <p className="text-sm text-destructive">{error}</p>}

                    <DialogFooter>
                      <Button type="submit" disabled={createMutation.isPending || form.sociedad_ids.length === 0}>
                        <UserPlus className="size-4" />
                        Agregar Administrador
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            ) : (
              <Dialog
                open={operOpen}
                onOpenChange={(next) => { setOperOpen(next); if (!next) { setOperForm(emptyOperForm); setOperError(null) } }}
              >
                <DialogTrigger asChild>
                  <Button disabled={!empresaQuery.data?.sociedades.length}>
                    <UserPlus className="size-4" />
                    Agregar Operador
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <form onSubmit={handleOperSubmit} className="flex flex-col gap-4">
                    <DialogHeader>
                      <DialogTitle>Agregar Operador</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-1.5">
                      <Label>Sociedad</Label>
                      <Select
                        value={operForm.sociedad_id}
                        onValueChange={(v) => setOperForm({ ...operForm, sociedad_id: v })}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Elegí una Sociedad" />
                        </SelectTrigger>
                        <SelectContent>
                          {empresaQuery.data?.sociedades.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="oper-nombre">Nombre</Label>
                      <Input
                        id="oper-nombre"
                        required
                        value={operForm.nombre}
                        onChange={(e) => setOperForm({ ...operForm, nombre: e.target.value })}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="oper-email">Email</Label>
                      <Input
                        id="oper-email"
                        type="email"
                        required
                        value={operForm.email}
                        onChange={(e) => setOperForm({ ...operForm, email: e.target.value })}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="oper-password">Contraseña</Label>
                      <Input
                        id="oper-password"
                        type="text"
                        required
                        minLength={8}
                        value={operForm.password}
                        onChange={(e) => setOperForm({ ...operForm, password: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Mínimo 8 caracteres. Pasale este email y contraseña a la persona para que entre.
                      </p>
                    </div>

                    {operError && <p className="text-sm text-destructive">{operError}</p>}

                    <DialogFooter>
                      <Button type="submit" disabled={createOperMutation.isPending || !operForm.sociedad_id}>
                        <UserPlus className="size-4" />
                        Agregar Operador
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        <TabsContent value="administradores">
          <Card className="overflow-hidden py-0">
            {administradoresQuery.isLoading && (
              <div className="flex flex-col gap-3 p-5">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-5/6" />
              </div>
            )}
            {administradoresQuery.isError && (
              <p className="p-5 text-sm text-destructive">No se pudieron cargar los Administradores.</p>
            )}
            {administradoresQuery.data && filteredAdmins.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                <UserCog className="size-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">
                  {search ? 'Ningún Administrador coincide con la búsqueda.' : 'Todavía no hay Administradores en esta Empresa.'}
                </p>
              </div>
            )}
            {pagedAdmins.length > 0 && (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Sociedades</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedAdmins.map((admin) => (
                      <TableRow key={admin.user_id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2.5">
                            <UserAvatar name={admin.nombre} seed={admin.email} size="sm" />
                            {admin.nombre}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{admin.email}</TableCell>
                        <TableCell>
                          <Badge variant={admin.is_active ? 'outline' : 'destructive'}>
                            {admin.is_active ? 'Activo' : 'Desactivado'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {admin.sociedad_ids.map((id) => (
                              <Badge key={id} variant="secondary">
                                {sociedadNombrePorId.get(id) ?? id}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setTransferTarget(admin)}>
                              <ArrowUpRight className="size-3.5" />
                              Transferir titularidad
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditTarget(admin)}>
                              Editar Sociedades
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={toggleActiveMutation.isPending}
                              onClick={() => toggleActiveMutation.mutate(admin)}
                            >
                              {admin.is_active ? (
                                <>
                                  <PowerOff className="size-3.5 text-destructive" />
                                  Desactivar
                                </>
                              ) : (
                                <>
                                  <Power className="size-3.5" />
                                  Activar
                                </>
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={removeMutation.isPending}
                              onClick={() => removeMutation.mutate(admin.user_id)}
                              title="Quitar de todas las Sociedades"
                            >
                              <Trash2 className="size-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination
                  page={adminPage}
                  pageCount={adminPageCount}
                  totalItems={filteredAdmins.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={setAdminPage}
                />
              </>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="operadores">
          <Card className="overflow-hidden py-0">
            {operadoresQuery.isLoading && (
              <div className="flex flex-col gap-3 p-5">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-5/6" />
              </div>
            )}
            {operadoresQuery.isError && <p className="p-5 text-sm text-destructive">No se pudieron cargar los Operadores.</p>}
            {operadoresQuery.data && filteredOpers.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                <UserRound className="size-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">
                  {search ? 'Ningún Operador coincide con la búsqueda.' : 'Todavía no hay Operadores en ninguna Sociedad.'}
                </p>
              </div>
            )}
            {pagedOpers.length > 0 && (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Sociedad</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedOpers.map((op) => (
                      <TableRow key={`${op.user_id}-${op.sociedad_id}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2.5">
                            <UserAvatar name={op.nombre} seed={op.email} size="sm" />
                            {op.nombre}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{op.email}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{op.sociedad_nombre}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setAssignTarget(op)}>
                              <Phone className="size-3.5" />
                              Asignar números
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={removeOperMutation.isPending}
                              onClick={() => removeOperMutation.mutate(op)}
                            >
                              <Trash2 className="size-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination
                  page={operPage}
                  pageCount={operPageCount}
                  totalItems={filteredOpers.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={setOperPage}
                />
              </>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editTarget} onOpenChange={(next) => !next && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sociedades de {editTarget?.nombre}</DialogTitle>
          </DialogHeader>
          <div className="flex max-h-52 flex-col gap-1.5 overflow-y-auto rounded-md border p-2">
            {empresaQuery.data?.sociedades.map((s) => (
              <label key={s.id} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted">
                <input
                  type="checkbox"
                  checked={editSociedadIds.includes(s.id)}
                  onChange={() => toggleEditSociedad(s.id)}
                />
                {s.nombre}
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>
              Cancelar
            </Button>
            <Button disabled={updateSociedadesMutation.isPending} onClick={() => updateSociedadesMutation.mutate()}>
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!transferTarget} onOpenChange={(next) => !next && setTransferTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transferir titularidad</DialogTitle>
            <DialogDescription>
              {transferTarget?.nombre} pasará a ser el Propietario de esta Empresa. Vos vas a perder acceso
              automático a sus Sociedades — el nuevo Propietario tendría que asignártelas de vuelta como
              Administrador si querés seguir con acceso. Esta acción no se deshace directamente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={transferMutation.isPending}
              onClick={() => transferTarget && transferMutation.mutate(transferTarget.user_id)}
            >
              Sí, transferir titularidad
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OperatorAssignmentsDialog
        member={assignTarget}
        sociedadId={assignTarget?.sociedad_id ?? null}
        onClose={() => setAssignTarget(null)}
      />
    </div>
  )
}
